// Die Art eines Hauses in Worten (A2 der Restliste Gestaltung; M4 und M7, Michael
// 21./22.10.2026; Regel design/glossar.md Abschnitt 2).
//
// "Haus" bleibt intern und erscheint nie im Text. Wo ein Name da ist, steht der
// Name; ohne Namen steht die Art aus platform.operating_context.kind, die die API
// als Code neben jedem Hausnamen liefert (#732). Eine unbekannte Art heisst
// "Verlag". Was ueber alle Haeuser spricht, sagt "die ganze Gruppe"; eine Menge
// von Haeusern gemischter Art heisst "Gesellschaften der Gruppe" (M7).
//
// EIN BAUSTEIN statt Deklination an jeder Stelle: Verlag und Vertrieb sind
// maskulin, Holding feminin, und jede Stelle braucht einen anderen Fall.

export type HausArt = 'publisher' | 'holding' | 'sales_organization';

/** Der Code aus der Antwort, oder null, wenn er fehlt oder unbekannt ist (dann "Verlag"). */
export function hausArtOf(value: unknown): HausArt | null {
  return value === 'publisher' || value === 'holding' || value === 'sales_organization' ? value : null;
}

/**
 * Die Faelle, die die Oberflaeche braucht:
 * - `nom` "Verlag", `plural` "Verlage"
 * - `dein` "dein Verlag", `deinen` "deinen Verlag", `deinem` "deinem Verlag", `deines` "deines Verlags"
 * - `einen` "einen Verlag", `einem` "einem Verlag", `ein` "ein Verlag"
 * - `der` das Relativpronomen allein: "der" (Verlag, Vertrieb), "die" (Holding)
 */
export type Fall = 'nom' | 'plural' | 'dein' | 'deinen' | 'deinem' | 'deines' | 'ein' | 'einen' | 'einem' | 'der';

const WOERTER: Readonly<Record<HausArt, Readonly<Record<Fall, string>>>> = Object.freeze({
  publisher: {
    nom: 'Verlag',
    plural: 'Verlage',
    dein: 'dein Verlag',
    deinen: 'deinen Verlag',
    deinem: 'deinem Verlag',
    deines: 'deines Verlags',
    ein: 'ein Verlag',
    einen: 'einen Verlag',
    einem: 'einem Verlag',
    der: 'der',
  },
  holding: {
    nom: 'Holding',
    plural: 'Holdings',
    dein: 'deine Holding',
    deinen: 'deine Holding',
    deinem: 'deiner Holding',
    deines: 'deiner Holding',
    ein: 'eine Holding',
    einen: 'eine Holding',
    einem: 'einer Holding',
    der: 'die',
  },
  sales_organization: {
    nom: 'Vertrieb',
    plural: 'Vertriebe',
    dein: 'dein Vertrieb',
    deinen: 'deinen Vertrieb',
    deinem: 'deinem Vertrieb',
    deines: 'deines Vertriebs',
    ein: 'ein Vertrieb',
    einen: 'einen Vertrieb',
    einem: 'einem Vertrieb',
    der: 'der',
  },
});

/** Die Art in Worten im gewuenschten Fall; ohne Art "Verlag". */
export function artWort(art: HausArt | null | undefined, fall: Fall): string {
  return WOERTER[art ?? 'publisher'][fall];
}

/**
 * Die gemeinsame Art einer Menge von Haeusern, oder "gemischt" (M7). Eine leere
 * Menge und unbekannte Arten zaehlen als "Verlag", wie ein einzelnes Haus.
 */
export function gemeinsameArt(arten: readonly (HausArt | null | undefined)[]): HausArt | 'gemischt' {
  const verschieden = new Set(arten.map((art) => art ?? 'publisher'));
  if (verschieden.size > 1) return 'gemischt';
  return [...verschieden][0] ?? 'publisher';
}

/** Das Feldwort ueber einer Auswahl von Haeusern: "Verlag", "Holding", "Vertrieb", gemischt "Gesellschaft" (M7). */
export function auswahlWort(arten: readonly (HausArt | null | undefined)[]): string {
  const art = gemeinsameArt(arten);
  return art === 'gemischt' ? 'Gesellschaft' : artWort(art, 'nom');
}

/**
 * Eine Anzahl Haeuser: "einen Verlag", "3 Verlage", gemischt "3 Gesellschaften der Gruppe".
 * Fuer "freigeschaltet für …", wenn die Namen fehlen.
 */
export function anzahlWort(arten: readonly (HausArt | null | undefined)[]): string {
  const art = gemeinsameArt(arten);
  if (art === 'gemischt') return `${arten.length} Gesellschaften der Gruppe`;
  return arten.length === 1 ? artWort(art, 'einen') : `${arten.length} ${artWort(art, 'plural')}`;
}

/**
 * Eine Menge ohne Zahl: "Verlage", "Holdings", gemischt oder unbekannt "Gesellschaften der Gruppe".
 * Fuer "so viele … auf einmal"; ohne geladene Arten bleibt es bei der Gruppe.
 */
export function mengeWort(arten: readonly (HausArt | null | undefined)[] | undefined): string {
  if (arten === undefined || arten.length === 0) return 'Gesellschaften der Gruppe';
  const art = gemeinsameArt(arten);
  return art === 'gemischt' ? 'Gesellschaften der Gruppe' : artWort(art, 'plural');
}

/** Namen als Aufzaehlung: "Verlag Nord", "Verlag Nord und Holding Süd", "A, B und C". */
export function namenListe(namen: readonly string[]): string {
  if (namen.length <= 1) return namen[0] ?? '';
  return `${namen.slice(0, -1).join(', ')} und ${namen[namen.length - 1]}`;
}
